import { Color, IconMinus, IconPlus, colors } from '@cabonline/ui-shared';
import {
  Dispatch,
  FC,
  PropsWithChildren,
  ReactNode,
  SetStateAction,
  SyntheticEvent,
  useCallback,
  useId,
} from 'react';

import { Button } from '../button';
import { ErrorText } from '../error-text';
import { Text } from '../text';
import {
  HeaderSize,
  HeaderStyle,
  StyledActionWrapper,
  StyledBody,
  StyledContainer,
  StyledHeader,
  StyledHeaderActions,
  StyledHeaderContainer,
  StyledHeaderRow,
  StyledInnerItem,
  StyledInnerItemHeader,
  StyledInnerWrap,
  StyledStatusPill,
  StyledStatusPillWrapper,
} from './styles';
import {
  AccordionActionButtonProps,
  AccordionBodyProps,
  AccordionHeaderProps,
  AccordionProps,
  AccordionStatusPillProps,
  AccordionTitleContentProps,
  AccordionTitleIconProps,
  StatusPillValue,
} from './types';

const getPillColor = (pill: StatusPillValue): Color => {
  return typeof pill === 'object' ? pill.color || colors.primary : colors.primary;
};

const getPillLabel = (pill: StatusPillValue): ReactNode => {
  return typeof pill === 'object' ? pill.label : pill;
};

const AccordionTitleIcon: FC<AccordionTitleIconProps> = ({
  clickable,
  isExpanded,
  expandedColor,
}) => {
  if (!clickable) return null;
  return isExpanded ? (
    <IconMinus color={expandedColor} />
  ) : (
    <IconPlus color={colors.black} />
  );
};

const AccordionTitleContent = ({
  title,
  titleAs,
  isExpanded,
  expandedColor,
}: AccordionTitleContentProps): React.ReactNode => {
  if (typeof title !== 'string') return title;
  return (
    <Text
      as={titleAs}
      weight="extraBold"
      size="l"
      color={isExpanded ? expandedColor : colors.black}
    >
      {title}
    </Text>
  );
};

const SinglePill: FC<{ pill: StatusPillValue; index: number }> = ({ pill, index }) => (
  <StyledStatusPill key={index} size={24} backgroundColor={getPillColor(pill)}>
    {getPillLabel(pill)}
  </StyledStatusPill>
);

const AccordionStatusPill: FC<AccordionStatusPillProps> = ({ statusPill }) => {
  if (statusPill === undefined) return null;
  const pills = Array.isArray(statusPill) ? statusPill : [statusPill];
  return <StyledStatusPillWrapper>{pills.map((pill, i) => <SinglePill key={i} pill={pill} index={i} />)}</StyledStatusPillWrapper>;
};

const AccordionHeader: FC<AccordionHeaderProps> = ({
  title,
  titleAs,
  isExpanded,
  expandedColor,
  errorMessage,
  statusPill,
  clickable,
  headerStyle,
  headerSize,
  panelId,
  onHeaderClick,
  headerActions,
}) => {
  const hasError = Boolean(errorMessage);

  return (
    <StyledHeaderContainer headerSize={headerSize} hasError={hasError}>
      <StyledHeader
        headerStyle={headerStyle}
        headerSize={headerSize}
        isExpanded={isExpanded}
        onClick={onHeaderClick}
        tabIndex={clickable ? 0 : -1}
        clickable={clickable}
        aria-expanded={clickable ? isExpanded : undefined}
        aria-controls={clickable ? panelId : undefined}
      >
        <StyledHeaderRow marginBottom={hasError}>
          <AccordionTitleIcon
            clickable={clickable}
            isExpanded={isExpanded}
            expandedColor={expandedColor}
          />
          <AccordionTitleContent
            title={title}
            titleAs={titleAs}
            isExpanded={isExpanded}
            expandedColor={expandedColor}
          />
          <AccordionStatusPill statusPill={statusPill} />
        </StyledHeaderRow>
        {errorMessage && (
          <StyledHeaderRow>
            <ErrorText darkMode={isExpanded && headerStyle === 'default'}>
              {errorMessage}
            </ErrorText>
          </StyledHeaderRow>
        )}
      </StyledHeader>
      {headerActions && (
        <StyledHeaderActions>{headerActions}</StyledHeaderActions>
      )}
    </StyledHeaderContainer>
  );
};

const AccordionActionButton: FC<AccordionActionButtonProps> = ({ actionButton }) => (
  <StyledActionWrapper>
    <Button
      label={actionButton.label}
      testID={actionButton.testID}
      variant="tertiary"
      onClick={actionButton.onClick}
      disabled={actionButton.disabled}
    />
  </StyledActionWrapper>
);

const AccordionBody: FC<AccordionBodyProps> = ({
  isExpanded,
  headerStyle,
  description,
  actionButton,
  children,
  id,
}) => {
  const hasDescription = Boolean(description);
  const hasActionButton = Boolean(actionButton);

  return (
    <StyledBody id={id} isExpanded={isExpanded} headerStyle={headerStyle} hidden={!isExpanded}>
      <StyledInnerWrap>
        <StyledInnerItem>
          {hasDescription && (
            <StyledInnerItemHeader>
              <Text marginBottom="s">{description}</Text>
              {hasActionButton && <AccordionActionButton actionButton={actionButton!} />}
            </StyledInnerItemHeader>
          )}
          {children}
        </StyledInnerItem>
        {!hasDescription && hasActionButton && (
          <AccordionActionButton actionButton={actionButton!} />
        )}
      </StyledInnerWrap>
    </StyledBody>
  );
};

export const Accordion: FC<AccordionProps> = ({
  children,
  isExpanded,
  setIsExpanded,
  title,
  titleAs = 'div',
  actionButton,
  description,
  headerStyle = 'default',
  headerSize = 'default',
  statusPill,
  errorMessage,
  id = '',
  testID,
  clickable = true,
  headerActions,
}) => {
  const expandedColor = headerStyle === 'outline' ? colors.black : colors.white;
  const panelId = useId();

  const handleHeaderClick = useCallback(
    (e: SyntheticEvent) => {
      e.preventDefault();
      if (clickable && setIsExpanded) {
        setIsExpanded(!isExpanded);
      }
    },
    [clickable, isExpanded, setIsExpanded],
  );

  return (
    <StyledContainer id={id} isExpanded={isExpanded} data-testid={testID}>
      <AccordionHeader
        title={title}
        titleAs={titleAs}
        isExpanded={isExpanded}
        expandedColor={expandedColor}
        errorMessage={errorMessage}
        statusPill={statusPill}
        clickable={clickable}
        headerStyle={headerStyle}
        headerSize={headerSize}
        panelId={panelId}
        onHeaderClick={handleHeaderClick}
        headerActions={headerActions}
      />
      <AccordionBody
        id={panelId}
        isExpanded={isExpanded}
        headerStyle={headerStyle}
        description={description}
        actionButton={actionButton}
      >
        {children}
      </AccordionBody>
    </StyledContainer>
  );
};
